import { Button, Card, Col, List, Modal, Result, Row, Select, Space, Spin, Statistic, Typography } from 'antd';
import {
  MailOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import React, { CSSProperties, useCallback, useContext, useEffect, useState } from 'react';
import { Grid, IntegrationExecution } from '../../models';
import { apiClient, hasGridsPageRole } from '../../utils';
import { UserContext } from '../contexts';
import { SearchPanel } from '../layout';
import { ExecutionRecordsPage } from '../admin/integrations/execution-records-page';
import { Gauge } from '@ant-design/charts';

export function WelcomeWidget() {
  const { Title, Text, Paragraph } = Typography;

  return (
    <Card
      bordered={false}
      style={{
        height: '100%',
        overflow: 'auto',
      }}
      bodyStyle={{
        padding: '16px',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
            Hoşgeldiniz!
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            Ana Veri Yönetimi Uygulaması'na hoşgeldiniz.
          </Text>
        </div>

        {/* Info Cards */}
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Card
              size="small"
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Space align="start" size="small">
                <div
                  style={{
                    background: '#fff7e6',
                    borderRadius: '50%',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ffd591',
                  }}
                >
                  <QuestionCircleOutlined style={{ fontSize: '18px', color: '#faad14' }} />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                    Yardım ve Destek
                  </Text>
                  <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
                    Uygulama ile ilgili bilgi almak için yardım sayfasını ziyaret edebilirsiniz. Uygulamaya yönelik karşılaştığınız
                    sorunlar, talepler, öneriler ve şikayetler için "Portal &#62; Çağrı Sistemleri &#62; KVY (Kurumsal Veri Yönetimi) Çağrı
                    Sistemi" üzerinde talep açabilirsiniz.
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              size="small"
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Space align="start" size="small">
                <div
                  style={{
                    background: '#e6f7ff',
                    borderRadius: '50%',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #91d5ff',
                  }}
                >
                  <MailOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                    İletişim
                  </Text>
                  <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
                    Diğer görüşleriniz için{' '}
                    <a href="mailto:anaveridestek@rsnet2.com.tr" style={{ color: '#1890ff' }}>
                      anaveridestek@rsnet2.com.tr
                    </a>{' '}
                    iletişim adresini kullanabilirsiniz.
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <SafetyCertificateOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
          <Text type="secondary" style={{ marginLeft: '6px', fontSize: '11px' }}>
            Kurumsal Veri Yönetimi Sistemi
          </Text>
        </div>
      </Space>
    </Card>
  );
}

export function StatisticsWidget() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [mdmStats, setMdmStats] = useState<any>(null);
  const [cacheTimestamp, setCacheTimestamp] = useState<string>('');

  const fetchStatistics = async (forceRefresh: boolean = false) => {
    setLoading(true);
    try {
      if (!forceRefresh) {
        // Check for cached data
        const cachedData = localStorage.getItem('mdm_statistics_cache');
        const cacheTime = localStorage.getItem('mdm_statistics_cache_time');

        if (cachedData && cacheTime) {
          const cacheAge = Date.now() - parseInt(cacheTime);
          const oneHourInMs = 60 * 60 * 1000;

          // Use cached data if less than 1 hour old
          if (cacheAge < oneHourInMs) {
            setMdmStats(JSON.parse(cachedData));
            setCacheTimestamp(moment(parseInt(cacheTime)).format('DD.MM.YYYY HH:mm'));
            setForbidden(false);
            setLoading(false);
            return;
          }
        }
      }

      // Fetch fresh data if no cache or cache expired or forced refresh
      const response = await apiClient.get('dashboard/statistics');
      setMdmStats(response.data);
      setForbidden(false);

      // Cache the data
      const timestamp = Date.now();
      localStorage.setItem('mdm_statistics_cache', JSON.stringify(response.data));
      localStorage.setItem('mdm_statistics_cache_time', timestamp.toString());
      setCacheTimestamp(moment(timestamp).format('DD.MM.YYYY HH:mm'));
    } catch (error: any) {
      if (error.response?.status === 403) {
        setForbidden(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const titleStyle: CSSProperties = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '12px',
    fontWeight: 500,
  };

  const headerTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <BarChartOutlined style={{ fontSize: '16px' }} />
      <span style={{ fontSize: '14px', fontWeight: 600 }}>İstatistikler</span>
    </div>
  );

  return (
    <Card
      title={headerTitle}
      size="small"
      bordered={false}
      style={{
        height: '100%',
        overflow: 'auto',
      }}
      bodyStyle={{ padding: '12px' }}
    >
      <div style={{ height: '100%', overflow: 'auto' }}>
        {!forbidden ? (
          <Row gutter={[8, 8]} style={{ width: '100%' }}>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f9f0ff 0%, #ffffff 100%)',
                  border: '1px solid #d3adf7',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <DatabaseOutlined style={{ fontSize: '16px', color: '#722ed1' }} />
                      <span style={{ color: '#722ed1', fontWeight: 600 }}>Veri seti sayısı</span>
                    </div>
                  }
                  value={mdmStats?.datasetCount}
                  precision={0}
                  valueStyle={{ color: '#722ed1', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                  border: '1px solid #b7eb8f',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <FileTextOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                      <span style={{ color: '#52c41a', fontWeight: 600 }}>Toplam kayıt sayısı</span>
                    </div>
                  }
                  value={mdmStats?.totalRecords}
                  precision={0}
                  valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                  border: '1px solid #91d5ff',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <PlusOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                      <span style={{ color: '#1890ff', fontWeight: 600 }}>Günlük yeni kayıtlar</span>
                    </div>
                  }
                  value={mdmStats?.todayCreated}
                  precision={0}
                  valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)',
                  border: '1px solid #ffe58f',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <EditOutlined style={{ fontSize: '16px', color: '#faad14' }} />
                      <span style={{ color: '#faad14', fontWeight: 600 }}>Günlük güncellenen kayıtlar</span>
                    </div>
                  }
                  value={mdmStats?.todayModified}
                  precision={0}
                  valueStyle={{ color: '#faad14', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #e6fffb 0%, #ffffff 100%)',
                  border: '1px solid #87e8de',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <ClockCircleOutlined style={{ fontSize: '16px', color: '#13c2c2' }} />
                      <span style={{ color: '#13c2c2', fontWeight: 600 }}>Bekleyen görevler</span>
                    </div>
                  }
                  value={mdmStats?.pendingTasks}
                  precision={0}
                  valueStyle={{ color: '#13c2c2', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #fff2f0 0%, #ffffff 100%)',
                  border: '1px solid #ffccc7',
                  cursor: 'default',
                }}
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Statistic
                  title={
                    <div style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <ExclamationCircleOutlined style={{ fontSize: '16px', color: '#f5222d' }} />
                      <span style={{ color: '#f5222d', fontWeight: 600 }}>Süresi geçen görevler</span>
                    </div>
                  }
                  value={mdmStats?.overdueTasks}
                  precision={0}
                  valueStyle={{ color: '#f5222d', fontSize: '20px', fontWeight: 'bold' }}
                  loading={loading}
                />
              </Card>
            </Col>
          </Row>
        ) : (
          <Result status="warning" subTitle="İstatistikleri görüntülemek için yetkiniz bulunmuyor." />
        )}
      </div>
      {cacheTimestamp && !forbidden && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
            marginTop: '8px',
            marginRight: '0.5rem',
          }}
        >
          <span
            style={{
              fontSize: '9px',
              color: '#8c8c8c',
              fontStyle: 'italic',
            }}
          >
            {cacheTimestamp} itibariyle
          </span>
          <span>
            <ReloadOutlined style={{ fontSize: '10px' }} onClick={() => fetchStatistics(true)} />
          </span>
        </div>
      )}
    </Card>
  );
}

export function IntegrationsWidget() {
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [executions, setExecutions] = useState<IntegrationExecution[]>();
  const [forbidden, setForbidden] = useState(false);
  const [executionDetailModal, setExecutionDetailModal] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<IntegrationExecution>();
  const [executionRecordsModal, setExecutionRecordsModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState(1);
  const [filterType, setFilterType] = useState<'all' | 'successful' | 'error'>('all');

  const loadExecutions = useCallback(
    (days: number = selectedDays) => {
      setExecutionsLoading(true);
      // Get executions from the selected number of days
      const from = moment().subtract(days, 'days').format('YYYY-MM-DD');

      apiClient
        .get(`integrations/executions?from=${from}`)
        .then(response => {
          setExecutions(response.data.rows as IntegrationExecution[]);
          setExecutionsLoading(false);
        })
        .catch(reason => {
          setExecutionsLoading(false);
          if (reason?.response?.status === 403) {
            setForbidden(true);
          }
        });
    },
    [selectedDays],
  );

  const handleDaysChange = (days: number) => {
    setSelectedDays(days);
    loadExecutions(days);
  };

  const handleFilterChange = (type: 'all' | 'successful' | 'error') => {
    setFilterType(type);
  };

  const filteredExecutions = executions?.filter(execution => {
    if (filterType === 'successful') return !execution.hasError;
    if (filterType === 'error') return execution.hasError;
    return true; // 'all'
  });

  const executionCounts = {
    all: executions?.length || 0,
    successful: executions?.filter(e => !e.hasError).length || 0,
    error: executions?.filter(e => e.hasError).length || 0,
  };

  // Group executions by day
  const groupedExecutions = filteredExecutions?.reduce((groups, execution) => {
    const day = moment(execution.createdAt).format('DD.MM.yyyy');
    if (!groups[day]) {
      groups[day] = [];
    }
    groups[day].push(execution);
    return groups;
  }, {} as Record<string, IntegrationExecution[]>);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  const dayOptions = () => {
    return (
      <Select value={selectedDays} style={{ width: 100 }} onChange={handleDaysChange} size="small">
        <Select.Option value={1}>1 gün</Select.Option>
        <Select.Option value={3}>3 gün</Select.Option>
        <Select.Option value={5}>5 gün</Select.Option>
      </Select>
    );
  };

  const executionLegend = () => {
    return (
      <Space size="small">
        <Button
          type="text"
          size="small"
          onClick={() => handleFilterChange('all')}
          style={{
            fontWeight: filterType === 'all' ? 'bold' : 'normal',
            padding: '0 4px',
          }}
        >
          Tümü: {executionCounts.all}
        </Button>
        <span style={{ color: '#d9d9d9' }}>|</span>
        <Button
          type="text"
          size="small"
          onClick={() => handleFilterChange('successful')}
          style={{
            color: '#52c41a',
            fontWeight: filterType === 'successful' ? 'bold' : 'normal',
            padding: '0 4px',
          }}
        >
          Başarılı: {executionCounts.successful}
        </Button>
        <span style={{ color: '#d9d9d9' }}>|</span>
        <Button
          type="text"
          size="small"
          onClick={() => handleFilterChange('error')}
          style={{
            color: '#ff4d4f',
            fontWeight: filterType === 'error' ? 'bold' : 'normal',
            padding: '0 4px',
          }}
        >
          Hatalı: {executionCounts.error}
        </Button>
      </Space>
    );
  };

  const openExecutionDetailModal = (execution: IntegrationExecution) => {
    setSelectedExecution(execution);
    setExecutionDetailModal(true);
  };

  const closeExecutionDetailModal = () => {
    setExecutionDetailModal(false);
    setSelectedExecution(undefined);
  };

  const openExecutionRecordsModal = () => {
    setExecutionRecordsModal(true);
  };

  const closeExecutionRecordsModal = () => {
    setExecutionRecordsModal(false);
  };

  const onErrorStackClick = (stack: string) => {
    Modal.confirm({
      title: 'Hata detayları',
      content: stack,
      okText: 'Tamam',
      cancelText: <></>,
      width: '30%',
    });
  };

  return (
    <Card
      title="Aktarımlar"
      size="small"
      bordered={false}
      style={{ height: 'calc(100% - 1rem)', overflowY: 'scroll', overflowX: 'hidden' }}
      extra={
        <Space size={16}>
          {!executionsLoading && !forbidden && executionLegend()}
          {dayOptions()}
        </Space>
      }
    >
      <Spin spinning={executionsLoading}>
        {!forbidden ? (
          <div>
            {groupedExecutions &&
              Object.keys(groupedExecutions)
                .sort((a, b) => moment(b, 'DD.MM.yyyy').valueOf() - moment(a, 'DD.MM.yyyy').valueOf())
                .map((day, dayIndex) => (
                  <div key={day} style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        backgroundColor: '#f5f5f5',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: '#595959',
                        borderLeft: '3px solid #1890ff',
                      }}
                    >
                      {day}
                    </div>
                    <List
                      size="small"
                      dataSource={groupedExecutions[day]}
                      renderItem={(execution: IntegrationExecution) => (
                        <List.Item
                          style={{
                            padding: '8px 12px',
                            borderRadius: '4px',
                            marginBottom: '4px',
                            border: '1px solid #f0f0f0',
                            backgroundColor: '#fafafa',
                          }}
                          actions={[
                            <InfoCircleOutlined
                              key="info"
                              style={{ cursor: 'pointer', color: '#1890ff' }}
                              onClick={() => openExecutionDetailModal(execution)}
                              title="Detayları görüntüle"
                            />,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <div
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: execution.hasError ? '#ff4d4f' : '#52c41a',
                                  marginTop: '6px',
                                }}
                              />
                            }
                            title={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span
                                  style={{
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                  }}
                                >
                                  {moment(execution.createdAt).format('HH:mm:ss')}
                                </span>
                                <span
                                  style={{
                                    textDecoration: execution.integration.deletedAt ? 'line-through' : 'inherit',
                                    fontSize: '14px',
                                  }}
                                >
                                  {execution.integration.name}
                                </span>
                              </div>
                            }
                            description={
                              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                {execution.hasError ? 'Hatalı' : 'Başarılı'} •
                                {execution.isManuallyTriggered ? ` Elle (${execution.executedBy?.fullName || 'Bilinmiyor'})` : ' Otomatik'}
                                {!execution.hasError && execution.recordCount && ` • ${execution.recordCount} kayıt`}
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                ))}
          </div>
        ) : (
          <Result status="warning" subTitle="Aktarımları görüntülemek için yetkiniz bulunmuyor." />
        )}
      </Spin>

      <Modal
        title="Aktarım detayları"
        visible={executionDetailModal}
        onCancel={closeExecutionDetailModal}
        footer={[
          <Button key="close" onClick={closeExecutionDetailModal}>
            Kapat
          </Button>,
        ]}
        width={700}
      >
        {selectedExecution && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <strong>Entegrasyon adı:</strong>
                <div>{selectedExecution.integration.name}</div>
              </Col>
              <Col span={12}>
                <strong>Durum:</strong>
                <div style={{ color: selectedExecution.hasError ? '#ff4d4f' : '#52c41a' }}>
                  {selectedExecution.hasError ? 'Hatalı' : 'Başarılı'}
                </div>
              </Col>
              <Col span={12}>
                <strong>Çalıştırılma tarihi:</strong>
                <div>{moment(selectedExecution.createdAt).format('DD.MM.yyyy HH:mm:ss')}</div>
              </Col>
              <Col span={12}>
                <strong>Çalıştırılma şekli:</strong>
                <div>{selectedExecution.isManuallyTriggered ? `Elle (${selectedExecution.executedBy?.fullName})` : 'Otomatik'}</div>
              </Col>
              <Col span={12}>
                <strong>Dosya içe aktarma:</strong>
                <div>{selectedExecution.isFileImport ? 'Evet' : 'Hayır'}</div>
              </Col>
              <Col span={12}>
                <strong>Kayıt sayısı:</strong>
                <div>
                  <Button type="link" style={{ padding: 0, height: 'auto', fontSize: '14px' }} onClick={openExecutionRecordsModal}>
                    {selectedExecution.recordCount || 0}
                  </Button>{' '}
                  (Toplam: {selectedExecution.totalRecordCount || 0})
                </div>
              </Col>
              {selectedExecution.executedBy && (
                <Col span={12}>
                  <strong>Çalıştıran Kullanıcı:</strong>
                  <div>{selectedExecution.executedBy.fullName}</div>
                </Col>
              )}
              {selectedExecution.hasError && selectedExecution.errorMessage && (
                <Col span={24}>
                  <strong>Hata mesajı:</strong>
                  <div>
                    <span style={{ color: '#ff4d4f' }}>{selectedExecution.errorMessage}</span>
                    <Button type="link" onClick={() => onErrorStackClick(selectedExecution.errorStack)}>
                      Detaylar
                    </Button>
                  </div>
                </Col>
              )}
              {selectedExecution.filter && selectedExecution.filter.length > 0 && (
                <Col span={24}>
                  <strong>Filtre:</strong>
                  <div
                    style={{
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      padding: '8px',
                      marginTop: '4px',
                      borderRadius: '4px',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(selectedExecution.filter, null, 2)}</pre>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>

      <Modal
        title="Aktarım Kayıtları"
        visible={executionRecordsModal}
        onCancel={closeExecutionRecordsModal}
        footer={[
          <Button key="close" onClick={closeExecutionRecordsModal}>
            Kapat
          </Button>,
        ]}
        width={1200}
        style={{ top: 20 }}
      >
        {selectedExecution && <ExecutionRecordsPage integrationExecution={selectedExecution} />}
      </Modal>
    </Card>
  );
}

export function SavedSearchesWidget() {
  const { user } = useContext(UserContext);

  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchModalText, setSearchModalText] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [savedSearches, setSavedSearches] = useState<string[]>();

  const loadSavedExecutions = () => {
    setLoading(true);
    apiClient
      .get('users/me/settings/searches')
      .then(response => {
        setSavedSearches(response.data as string[]);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSavedExecutions();
  }, []);

  const openSearchModal = (value: string) => {
    setSearchModalText(value);
    setSearchModalOpen(true);
  };

  const closeSearchModal = () => {
    setSearchModalOpen(false);
    setSearchModalText(undefined);
  };

  if (!hasGridsPageRole(user)) {
    return null;
  }

  return (
    <>
      <Card title="Kaydedilmiş aramalar" size="small" bordered={false} style={{ height: '100%', overflowY: 'auto' }}>
        <List
          loading={loading}
          dataSource={savedSearches}
          renderItem={(item: string) => (
            <List.Item>
              <Button type="link" onClick={() => openSearchModal(item)}>
                {item}
              </Button>
            </List.Item>
          )}
        />
      </Card>

      <Modal title="Arama" visible={searchModalOpen} footer={null} onCancel={closeSearchModal} destroyOnClose={true} width={'70%'}>
        <SearchPanel initialSearchValue={searchModalText} />
      </Modal>
    </>
  );
}

export function DataQualityWidget() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>();
  const [forbidden, setForbidden] = useState(false);
  const [requiredGauge, setRequiredGauge] = useState<any>({});
  const [validityGauge, setValidityGauge] = useState<any>({});
  const [cacheTimestamp, setCacheTimestamp] = useState<string>('');

  const { Text } = Typography;

  const loadData = (forceRefresh: boolean = false) => {
    setLoading(true);

    if (!forceRefresh) {
      // Check for cached data
      const cachedData = localStorage.getItem('mdm_data_quality_cache');
      const cacheTime = localStorage.getItem('mdm_data_quality_cache_time');
      const cacheDate = localStorage.getItem('mdm_data_quality_cache_date');
      const today = moment().format('YYYY-MM-DD');

      if (cachedData && cacheDate && cacheTime && cacheDate === today) {
        // Use cached data if it's from today
        const parsedData = JSON.parse(cachedData);
        setData(parsedData);
        setCacheTimestamp(moment(parseInt(cacheTime)).format('DD.MM.YYYY HH:mm'));
        setForbidden(false);
        setLoading(false);
        return;
      }
    }

    // Fetch fresh data if no cache or cache is from a previous day or forced refresh
    apiClient
      .get('dashboard/data-quality')
      .then(response => {
        setData(response.data);

        // Cache the data with today's date and current time
        const timestamp = Date.now();
        const today = moment().format('YYYY-MM-DD');
        localStorage.setItem('mdm_data_quality_cache', JSON.stringify(response.data));
        localStorage.setItem('mdm_data_quality_cache_date', today);
        localStorage.setItem('mdm_data_quality_cache_time', timestamp.toString());
        setCacheTimestamp(moment(timestamp).format('DD.MM.YYYY HH:mm'));
      })
      .catch(reason => {
        setLoading(false);
        if (reason?.response?.status === 403) {
          setForbidden(true);
        }
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadGauge = useCallback(
    (gridId?: string) => {
      const gridData = gridId && gridId !== 'all' ? data?.columns[gridId] : data;
      const configRequired = {
        percent: gridData.requiredColumnsCount ? gridData.requiredColumnsFilledCount / gridData.requiredColumnsCount : 1,
        range: {
          ticks: [0, 1 / 3, 1 / 2, 1],
          color: ['#F4664A', '#FAAD14', '#30BF78'],
        },
        indicator: {
          pointer: {
            style: {
              stroke: '#D0D0D0',
            },
          },
          pin: {
            style: {
              stroke: '#D0D0D0',
            },
          },
        },
        statistic: {
          content: {
            style: {
              fontSize: '10px',
              lineHeight: '22px',
            },
            customHtml: (container: HTMLElement, view: any, datum: any) => {
              container.innerHTML = `<div style="font-size:24px; color: ${
                datum.percent < 1 / 3 ? '#F4664A' : datum.percent < 1 / 2 ? '#FAAD14' : '#30BF78'
              }">%${Math.round(datum.percent * 100 * 100) / 100}</div>`;
            },
          },
        },
      };
      const configValidity = {
        percent: gridData.columnsWithRulesCount ? gridData.validColumnsCount / gridData.columnsWithRulesCount : 1,
        range: {
          ticks: [0, 1 / 3, 1 / 2, 1],
          color: ['#F4664A', '#FAAD14', '#30BF78'],
        },
        indicator: {
          pointer: {
            style: {
              stroke: '#D0D0D0',
            },
          },
          pin: {
            style: {
              stroke: '#D0D0D0',
            },
          },
        },
        statistic: {
          content: {
            style: {
              fontSize: '10px',
              lineHeight: '22px',
            },
            customHtml: (container: HTMLElement, view: any, datum: any) => {
              container.innerHTML = `<div style="font-size:24px; color: ${
                datum.percent < 1 / 3 ? '#F4664A' : datum.percent < 1 / 2 ? '#FAAD14' : '#30BF78'
              }">%${Math.round(datum.percent * 100 * 100) / 100}</div>`;
            },
          },
        },
      };
      setRequiredGauge(configRequired);
      setValidityGauge(configValidity);
      setLoading(false);
    },
    [data],
  );

  useEffect(() => {
    if (data) {
      loadGauge();
    }
  }, [data, loadGauge]);

  const grids = () => {
    return (
      <Select
        placeholder="Veri seti seçin"
        style={{ width: '300px' }}
        defaultValue="all"
        onChange={value => {
          loadGauge(value.toString());
        }}
      >
        <Select.Option key="all" value="all">
          Tümü
        </Select.Option>
        {Object.keys(data?.columns || {}).map((grid: any) => (
          <Select.Option key={grid} value={grid}>
            {grid}
          </Select.Option>
        ))}
      </Select>
    );
  };

  return (
    <Card
      title="Veri kalitesi"
      size="small"
      bordered={false}
      style={{ height: '100%', overflow: 'hidden' }}
      extra={[loading ? null : grids()]}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}>
          {!forbidden ? (
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                  <Text strong>Zorunlu veri</Text>
                  <Spin spinning={loading}>
                    <Gauge {...requiredGauge} height={200} />
                  </Spin>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                  <Text strong>Geçerli veri</Text>
                  <Spin spinning={loading}>
                    <Gauge {...validityGauge} height={200} />
                  </Spin>
                </Space>
              </Col>
            </Row>
          ) : (
            <Result status="warning" subTitle="Veri kalitesi bilgilerini görüntülemek için yetkiniz bulunmuyor." />
          )}
        </div>
        {cacheTimestamp && !forbidden && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '6px',
              marginTop: '1rem',
              marginRight: '0.5rem',
            }}
          >
            <span
              style={{
                fontSize: '9px',
                color: '#8c8c8c',
                fontStyle: 'italic',
              }}
            >
              {cacheTimestamp} itibariyle
            </span>
            <span>
              <ReloadOutlined style={{ fontSize: '10px' }} onClick={() => loadData(true)} />
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

export function WorkloadWidget() {
  const [loading, setLoading] = useState(false);
  const [workload, setWorkload] = useState<any[]>();
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    loadWorkload();
  }, []);

  const loadWorkload = () => {
    setLoading(true);
    apiClient
      .get('tasks/counts')
      .then(async response => {
        const gridData: any[] = response.data.filter((item: any) => {
          return item.cellChangeRequestsCount > 0;
        });
        setWorkload(gridData);
        setLoading(false);
      })
      .catch(reason => {
        setLoading(false);
        if (reason?.response?.status === 403) {
          setForbidden(true);
        }
      });
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ClockCircleOutlined style={{ fontSize: '16px' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Bekleyen görevler</span>
        </div>
      }
      size="small"
      bordered={false}
      style={{
        height: '100%',
        overflow: 'auto',
      }}
      bodyStyle={{ padding: '12px' }}
    >
      <div style={{ height: '100%', overflow: 'auto' }}>
        {!forbidden ? (
          loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {workload?.map((item: any) => (
                <Card
                  key={item.gridId}
                  size="small"
                  style={{
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: '#fafafa',
                  }}
                  bodyStyle={{ padding: '8px' }}
                  onClick={() => {
                    window.location.href = `/grids/${item.gridId}?withMyTasks=true`;
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1890ff',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.gridName}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#8c8c8c',
                        }}
                      >
                        Bekleyen görev sayısı
                      </div>
                    </div>
                    <div
                      style={{
                        background: '#e6fffb',
                        border: '1px solid #87e8de',
                        borderRadius: '12px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#13c2c2',
                        minWidth: '40px',
                        textAlign: 'center',
                      }}
                    >
                      {item.cellChangeRequestsCount}
                    </div>
                  </div>
                </Card>
              ))}
              {(!workload || workload.length === 0) && (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#8c8c8c',
                    fontSize: '12px',
                    padding: '20px',
                    fontStyle: 'italic',
                  }}
                >
                  Bekleyen görev bulunmuyor
                </div>
              )}
            </Space>
          )
        ) : (
          <Result status="warning" subTitle="Bekleyen görevleri görüntülemek için yetkiniz bulunmuyor." />
        )}
      </div>
    </Card>
  );
}

export function DataSetsWidget() {
  const [loading, setLoading] = useState(false);
  const [grids, setGrids] = useState<Grid[]>();

  useEffect(() => {
    loadGrids();
  }, []);

  const loadGrids = () => {
    setLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        setGrids(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
      });
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DatabaseOutlined style={{ fontSize: '16px' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Veri setleri</span>
        </div>
      }
      size="small"
      bordered={false}
      style={{
        height: '100%',
        overflow: 'auto',
      }}
      bodyStyle={{ padding: '12px' }}
    >
      <div style={{ height: '100%', overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {grids?.map((grid: Grid) => (
              <Card
                key={grid.id}
                size="small"
                style={{
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: '#fafafa',
                }}
                bodyStyle={{ padding: '8px' }}
                onClick={() => {
                  window.location.href = `/grids/${grid.id}`;
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#1890ff',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {grid.name}
                  </div>
                  {grid.description && (
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#8c8c8c',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {grid.description}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>
    </Card>
  );
}
